/**
 * File and save operations for the journal source.
 *
 * All operations target the user's journal source (feature='journal')
 * via /api/journal endpoints.
 *
 * Every function propagates failures. Callers that have a defined UI reaction
 * catch at the call site; nothing here turns "no network" into a null that
 * looks like "server said no".
 */

import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '@/shared/services/api';
import { registerReadPolicy } from '@shared/offline';

import { entryBodyUrl, idFromUrl, journalEntity } from './journalContent';
// The write policies register themselves; importing them here means they
// exist wherever this api does (main.tsx imports them too, for a boot flush).
import './journalWritePolicies';

import type { NotesFileContent, UpdateFrontmatterResult } from '@features/notes/services/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const JOURNAL_BASE = '/api/journal';

// Re-exported so a caller that has just written the entry can name the cache
// entry it made stale by the url these functions actually call.
export { entryBodyUrl } from './journalContent';

// An entry body is a document the editor holds under the cursor: serve the
// network when it answers, the cached copy only when it cannot. Never a
// cache-first swap under the user's hands. The entity ties the read to the
// entry's queued writes, so the cached copy is authoritative while any wait.
registerReadPolicy(/^\/api\/journal\/files\/by-id\/[^/]+$/, {
  strategy: 'network-first',
  ttlMs: 0,
  entity: url => journalEntity(idFromUrl(url)),
});

/** Save result from either path- or ID-based writes, returning the entry id and path. */
export interface JournalSaveResult {
  id: string;
  path: string;
  /** The revision the write moved the entry to; absent when the write was only queued. */
  revision?: string | null;
}

/** The `expected_revision` field for a write, present only when the caller holds one. */
const expecting = (revision: string | null | undefined): { expected_revision?: string } =>
  revision ? { expected_revision: revision } : {};

/**
 * Create a journal entry from a client id, a path and content.
 *
 * Calls POST /api/journal/files. The backend parses frontmatter, creates the
 * DB row (deriving title/date from the path), and returns the new entry. The
 * id is minted on the client so a create queued offline replays as the same
 * entry, not a duplicate.
 *
 * @param id - The client-generated UUID for the new entry.
 * @param path - The relative path for the new entry.
 * @param content - The markdown content to seed the entry with.
 * @returns The new entry's id, path, and revision (absent when only queued).
 * @throws When the request fails or the server rejects the create.
 */
export async function createJournalFile(
  id: string,
  path: string,
  content: string
): Promise<JournalSaveResult> {
  const entry = await apiPost<JournalSaveResult>(`${JOURNAL_BASE}/files`, { id, path, content });
  return { id: entry.id, path: entry.path, revision: entry.revision ?? null };
}

// ---------------------------------------------------------------------------
// ID-based API (uses entry UUID instead of file path)
// ---------------------------------------------------------------------------

/** Fetch a journal entry's content by its UUID. Throws on failure. */
export async function getJournalFileById(entryId: string): Promise<NotesFileContent> {
  return apiGet<NotesFileContent>(entryBodyUrl(entryId));
}

/**
 * Save a journal entry's body content by UUID. Returns the id and path; throws on failure.
 *
 * `expectedRevision` is the revision the caller last saw; the server rejects
 * the write with a 409 when the entry has moved past it.
 */
export async function saveJournalFileById(
  entryId: string,
  content: string,
  expectedRevision?: string | null
): Promise<JournalSaveResult> {
  return apiPut<JournalSaveResult>(entryBodyUrl(entryId), {
    content,
    ...expecting(expectedRevision),
  });
}

/** Update the frontmatter of a journal entry by UUID, guarded by the revision the caller holds. Throws on failure. */
export async function updateJournalFrontmatterById(
  entryId: string,
  frontmatter: Record<string, unknown>,
  expectedRevision?: string | null
): Promise<UpdateFrontmatterResult> {
  return apiPatch<UpdateFrontmatterResult>(`${entryBodyUrl(entryId)}/frontmatter`, {
    frontmatter,
    ...expecting(expectedRevision),
  });
}

/** Delete a journal entry by UUID. Resolves true on success; throws on failure. */
export async function deleteJournalFileById(entryId: string): Promise<boolean> {
  await apiDelete(entryBodyUrl(entryId));
  return true;
}
